import { useEffect } from 'react';
import api from '../../utils/api';

// Guard against double-injection (React StrictMode mounts twice in dev,
// and we never want to inject the same analytics/tags more than once).
let injected = false;

// Inject a raw HTML string (which may contain <script> tags) into the page.
// <script> elements created via innerHTML never execute, so we recreate them.
function injectHtml(html, position) {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  const target = position === 'head' ? document.head : document.body;
  const atStart = position === 'body_start';

  Array.from(temp.childNodes).forEach((node) => {
    let el = node;
    if (node.tagName === 'SCRIPT') {
      el = document.createElement('script');
      Array.from(node.attributes).forEach((a) => el.setAttribute(a.name, a.value));
      el.text = node.textContent;
    }
    if (atStart && target.firstChild) target.insertBefore(el, target.firstChild);
    else target.appendChild(el);
  });
}

function injectScriptSrc(src, attrs = {}) {
  const s = document.createElement('script');
  s.src = src;
  s.async = true;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

function injectInline(code) {
  const s = document.createElement('script');
  s.text = code;
  document.head.appendChild(s);
}

export default function TagInjector() {
  useEffect(() => {
    if (injected) return;
    injected = true;

    api.get('/settings/public').then(({ data }) => {
      const s = data.settings || {};

      // Google Analytics 4
      if (s.ga4_id) {
        injectScriptSrc(`https://www.googletagmanager.com/gtag/js?id=${s.ga4_id}`);
        injectInline(
          `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}` +
          `gtag('js',new Date());gtag('config','${s.ga4_id}');`
        );
      }

      // Google Tag Manager
      if (s.gtm_id) {
        injectInline(
          `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});` +
          `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';` +
          `j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);` +
          `})(window,document,'script','dataLayer','${s.gtm_id}');`
        );
      }

      // Meta (Facebook) Pixel
      if (s.meta_pixel_id) {
        injectInline(
          `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?` +
          `n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;` +
          `n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;` +
          `t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,` +
          `'script','https://connect.facebook.net/en_US/fbevents.js');` +
          `fbq('init','${s.meta_pixel_id}');fbq('track','PageView');`
        );
      }

      // Custom Tags / Scripts (admin "tag management")
      let tags = [];
      try { tags = s.custom_tags ? JSON.parse(s.custom_tags) : []; } catch { tags = []; }
      tags.forEach((t) => {
        if (t && t.code) injectHtml(t.code, t.position || 'head');
      });
    }).catch(() => {});
  }, []);

  return null;
}
